import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, DataTable, StatusBadge, ColumnSettingsButton, btnPrimary, colors } from '../../components/ui'
import type { Column } from '../../components/ui'
import { api } from '../../services/api'
import { useAuth } from '../../hooks/use-auth'
import { useAlertSummary } from '../../hooks/use-alert-summary'
import { livenessFromLastSeen, relativeTime, type EnvironmentRow } from '../fleet/fleet-helpers'
import { useTableColumns } from '../../hooks/use-table-columns'
import { type UserRole, roleSatisfies } from '@alpha-edge/shared'

const BASE_COLUMNS: readonly Column<EnvironmentRow>[] = [
  {
    key: 'name',
    header: 'Environment',
    render: (r) => <strong>{r.name}</strong>,
    sortable: true,
  },
  {
    key: 'environmentType',
    header: 'Type',
    render: (r) => r.environmentType,
  },
  {
    key: 'liveness',
    header: 'Status',
    render: (r) => <StatusBadge status={livenessFromLastSeen(r.lastSeenAt)} />,
  },
  {
    key: 'currentVersion',
    header: 'Version',
    render: (r) => r.currentVersion ?? '—',
  },
  {
    key: 'lastSeenAt',
    header: 'Last seen',
    render: (r) => relativeTime(r.lastSeenAt),
  },
]

const CUSTOMER_COLUMN: Column<EnvironmentRow> = {
  key: 'owner',
  header: 'Customer',
  render: (r) => r.ownerOrganizationName ?? '—',
  sortable: true,
}

export function LicenseListPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const alertSummary = useAlertSummary()
  const [rows, setRows] = useState<readonly EnvironmentRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedOrgId, setSelectedOrgId] = useState<string>('all')
  const { tableId: _, ...tableColumnProps } = useTableColumns('licenses')

  const role = user?.role as UserRole | undefined
  const isMedflowAdmin = role ? roleSatisfies(role, ['medflow_admin']) : false

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly EnvironmentRow[]>('/fleet/environments')
      .then((res) => {
        if (cancelled) return
        setRows(res.data ?? [])
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Failed to load environments')
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const orgOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of rows) {
      if (r.ownerOrganizationId && !seen.has(r.ownerOrganizationId)) {
        seen.set(r.ownerOrganizationId, r.ownerOrganizationName ?? r.ownerOrganizationId)
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [rows])

  const filteredRows = useMemo(() => {
    if (selectedOrgId === 'all') return rows
    return rows.filter((r) => r.ownerOrganizationId === selectedOrgId)
  }, [rows, selectedOrgId])

  const columns = useMemo(() => {
    if (isMedflowAdmin && selectedOrgId === 'all') return [CUSTOMER_COLUMN, ...BASE_COLUMNS]
    return BASE_COLUMNS
  }, [isMedflowAdmin, selectedOrgId])

  const customerLabel = (() => {
    if (!isMedflowAdmin && user?.organizationId) {
      const match = orgOptions.find((o) => o.id === user.organizationId)
      return match?.name ?? 'My Organization'
    }
    return null
  })()

  return (
    <div>
      <PageHeader
        title="Environments"
        subtitle={`${filteredRows.length} of ${rows.length}`}
        actions={
          <>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/licenses/new')} style={btnPrimary}>
              New Environment
            </button>
          </>
        }
      />

      {/* Organization filter — medflow_admin can choose; customer_admin is locked */}
      {!loading && orgOptions.length > 0 && (
        <div style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label htmlFor="org-filter" style={{ fontSize: '0.8125rem', fontWeight: 600, color: colors.text }}>
            Organization:
          </label>
          {isMedflowAdmin ? (
            <select
              id="org-filter"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              style={{
                padding: '0.35rem 0.5rem',
                fontSize: '0.8125rem',
                borderRadius: '4px',
                border: `1px solid ${colors.border}`,
                background: '#fff',
                minWidth: '200px',
              }}
            >
              <option value="all">All organizations</option>
              {orgOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          ) : (
            <select
              id="org-filter"
              disabled
              value={user?.organizationId ?? ''}
              style={{
                padding: '0.35rem 0.5rem',
                fontSize: '0.8125rem',
                borderRadius: '4px',
                border: `1px solid ${colors.border}`,
                background: '#f5f5f5',
                color: colors.text,
                minWidth: '200px',
                cursor: 'not-allowed',
              }}
            >
              <option value={user?.organizationId ?? ''}>{customerLabel ?? 'My Organization'}</option>
            </select>
          )}
        </div>
      )}

      {alertSummary.activeCount > 0 && (
        <div
          onClick={() => navigate('/licenses/alerts')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') navigate('/licenses/alerts')
          }}
          style={{
            padding: '0.6rem 1rem',
            marginBottom: '0.75rem',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 500,
            background: alertSummary.criticalCount > 0 ? '#fee2e2' : '#fef3c7',
            color: alertSummary.criticalCount > 0 ? '#dc2626' : '#92400e',
            border: `1px solid ${alertSummary.criticalCount > 0 ? '#fecaca' : '#fde68a'}`,
          }}
        >
          {alertSummary.activeCount} active alert{alertSummary.activeCount !== 1 ? 's' : ''}{' '}
          {alertSummary.criticalCount > 0 ? ` (${alertSummary.criticalCount} critical)` : ''} — click to view
        </div>
      )}
      {error && (
        <div
          style={{
            color: colors.danger,
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '3px',
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <DataTable
          columns={columns}
          rows={filteredRows}
          keyField="id"
          onRowClick={(r) => navigate(`/licenses/${r.id}`)}
          emptyMessage="No environments yet. Click New Environment to mint a license key for your first deployment."
          {...tableColumnProps}
        />
      )}
    </div>
  )
}
